import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLang } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useGravityToast } from '@/components/effects/GravityToast';
import api from '@/utils/api';
import { RankBadge } from '@/utils/ranks';
import ScrollReveal from '@/components/effects/ScrollReveal';
import { ArrowLeft, Pin, Lock, Eye, Send, MessageCircle } from 'lucide-react';

export default function ForumThread() {
  const { id } = useParams();
  const { t, localizedField } = useLang();
  const { user } = useAuth();
  const toast = useGravityToast();
  const [thread, setThread] = useState(null);
  const [replies, setReplies] = useState([]);
  const [newReply, setNewReply] = useState('');
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/forum/threads/${id}`).then(r => setThread(r.data)).catch(() => setNotFound(true));
    api.get(`/forum/replies/${id}`).then(r => setReplies(r.data)).catch(() => {});
  }, [id]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!newReply.trim()) return;
    try {
      const { data } = await api.post('/forum/replies', { thread_id: id, content: newReply });
      setReplies(prev => [...prev, data]);
      setNewReply('');
      toast.success('Reply posted');
    } catch { toast.error('Failed to post reply'); }
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4">
        <p className="text-warm-brown font-body">{t('common.notFound')}</p>
        <Link to="/forum" className="gold-button px-6 py-2.5 rounded-full font-body text-sm">{t('forum.title')}</Link>
      </div>
    );
  }
  if (!thread) return <div className="min-h-screen flex items-center justify-center"><div className="text-sacred-gold animate-gold-pulse">{t('common.loading')}</div></div>;

  return (
    <div data-testid="forum-thread-page" className="min-h-screen py-20 md:py-32 px-4">
      <div className="max-w-4xl mx-auto">
        <Link to={thread.category ? `/forum/${thread.category.slug}` : '/forum'} className="inline-flex items-center gap-2 text-sacred-gold font-body text-sm mb-6 hover:text-molten-gold">
          <ArrowLeft size={16} /> {localizedField(thread.category?.name) || t('forum.title')}
        </Link>

        <ScrollReveal>
          <div className="glass-card rounded-2xl p-6 md:p-8 mb-6" data-testid="thread-main">
            <div className="flex items-center gap-2 mb-3">
              {thread.is_pinned && <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-sacred-gold/10 text-sacred-gold text-xs"><Pin size={10} /> {t('forum.pinned')}</span>}
              {thread.is_locked && <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-dust-gray/20 text-dust-gray text-xs"><Lock size={10} /> {t('forum.locked')}</span>}
            </div>
            <h1 className="text-2xl sm:text-3xl font-display text-heading mb-4" data-testid="thread-title">{localizedField(thread.title)}</h1>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-sacred-gold/10 flex items-center justify-center text-sacred-gold font-bold">
                {thread.user?.display_name?.[0]?.toUpperCase() || '?'}
              </div>
              <div>
                <Link to={`/profile/${thread.user?.username}`} className="text-deep-brown font-body text-sm hover:text-sacred-gold">{thread.user?.display_name}</Link>
                <div className="flex items-center gap-2">
                  {thread.user?.rank && <RankBadge rank={thread.user.rank} size="sm" />}
                  <span className="text-text-muted/50 text-xs font-body">{new Date(thread.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="ml-auto flex items-center gap-3 text-text-muted/50 text-xs font-body">
                <span className="flex items-center gap-1"><Eye size={12} /> {thread.view_count}</span>
                <span className="flex items-center gap-1"><MessageCircle size={12} /> {thread.reply_count}</span>
              </div>
            </div>
            <div className="prose-gravity">
              <p className="text-deep-brown/80 font-body whitespace-pre-wrap">{localizedField(thread.content)}</p>
            </div>
          </div>
        </ScrollReveal>

        <div className="space-y-4 mb-8">
          {replies.map((reply, i) => (
            <ScrollReveal key={reply.id} delay={i * 0.05}>
              <div className="glass-card rounded-xl p-5" data-testid={`reply-${reply.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-sacred-gold/10 flex items-center justify-center text-sacred-gold text-xs font-bold">
                    {reply.user?.display_name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <Link to={`/profile/${reply.user?.username}`} className="text-deep-brown font-body text-sm hover:text-sacred-gold">{reply.user?.display_name}</Link>
                    {reply.user?.rank && <RankBadge rank={reply.user.rank} size="sm" />}
                  </div>
                  <span className="text-text-muted/50 text-xs font-body ml-auto">{new Date(reply.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-deep-brown/80 font-body text-sm pl-11 whitespace-pre-wrap">{reply.content}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>

        {user && !thread.is_locked && user.has_password && (
          <form onSubmit={handleReply} className="glass-card rounded-2xl p-6" data-testid="reply-form">
            <h3 className="text-lg font-display text-heading mb-4">{t('forum.postReply')}</h3>
            <textarea
              value={newReply}
              onChange={e => setNewReply(e.target.value)}
              placeholder={t('forum.writeReply')}
              rows={4}
              required
              className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm resize-none mb-4"
              data-testid="reply-input"
            />
            <button type="submit" className="gold-button px-5 py-2 rounded-full font-body text-sm flex items-center gap-2" data-testid="reply-submit">
              <Send size={16} /> {t('forum.postReply')}
            </button>
          </form>
        )}

        {user && !thread.is_locked && !user.has_password && (
          <div className="glass-card rounded-2xl p-6 text-center" data-testid="set-password-gate">
            <Lock size={20} className="text-sacred-gold mx-auto mb-3" />
            <p className="text-deep-brown font-body text-sm mb-4">
              Set a password to unlock full participation and reply in the forum.
            </p>
            <Link to="/auth/set-password" className="gold-button px-5 py-2 rounded-full font-body text-sm inline-flex items-center gap-2" data-testid="set-password-link">
              Set a password
            </Link>
          </div>
        )}

        {thread.is_locked && (
          <div className="glass-card rounded-xl p-4 text-center text-text-muted font-body text-sm">
            <Lock size={16} className="inline mr-2" /> This thread is locked
          </div>
        )}
      </div>
    </div>
  );
}
